import * as React from 'react';

import { cn } from '~/core/utils/cn';

type ButtonType = 'filled' | 'outlined' | 'transparent' | 'text' | 'link';
type Variant = 'primary' | 'surface';

export interface CustomButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  buttonType?: ButtonType;
  height?: string;
  width?: string;
  isLoading?: boolean;
  isFullWidth?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  dataName?: string;
  dataOrgId?: string | number;
  iconClassName?: string;
  spinnerClassName?: string;
}

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      variant = 'primary',
      buttonType = 'filled',
      height,
      width,
      isLoading = false,
      isFullWidth,
      iconLeft,
      iconRight,
      disabled,
      children,
      className,
      dataName,
      dataOrgId,
      iconClassName,
      spinnerClassName,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    const typeVariantStyles: Record<ButtonType, Record<Variant, string>> = {
      filled: {
        primary: 'bg-primaryDefault text-white hover:bg-primaryDark duration-300 ease-in-out	',
        surface: 'bg-neutralDefault text-white hover:opacity-90 duration-300 ease-in-out',
      },
      outlined: {
        primary:
          'border border-primaryDefault text-primaryDefault hover:bg-primaryExtraLight duration-300 ease-in-out',
        surface:
          'border border-neutralDefault text-neutralUltraDark hover:bg-neutralExtraLight bg-white duration-300 ease-in-out',
      },
      transparent: {
        primary:
          'bg-transparent text-primaryDefault hover:bg-primaryExtraLight duration-300 ease-in-out',
        surface:
          'bg-transparent text-neutralUltraDark hover:bg-neutralExtraLight duration-300 ease-in-out',
      },
      text: {
        primary:
          'bg-transparent text-primaryDefault font-semibold hover:text-primaryDark duration-300 ease-in-out',
        surface: 'bg-transparent text-neutralUltraDark duration-300 ease-in-out',
      },
      link: {
        primary: 'text-primaryDefault underline hover:opacity-80 duration-300 ease-in-out',
        surface: 'text-neutralUltraDark underline hover:opacity-80 duration-300 ease-in-out',
      },
    };

    const isTextLike = buttonType === 'text' || buttonType === 'link';

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        data-name={dataName && dataOrgId ? `${dataName}-${dataOrgId}` : dataName}
        className={cn(
          isTextLike
            ? 'font-inherit m-0 border-0 p-0 align-baseline text-inherit'
            : 'inline-flex items-center justify-center rounded-xl transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryDefault',
          typeVariantStyles[buttonType][variant],
          !isTextLike && (height || 'h-11 lg:h-12'), // Responsive height: 44px mobile/tablet, 48px desktop
          !isTextLike && width,
          isDisabled && 'cursor-not-allowed opacity-50',
          isFullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {isLoading ? (
          <span
            className={cn(
              'mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent',
              spinnerClassName
            )}
          />
        ) : iconLeft ? (
          <span className={`mr-2 ${iconClassName}`}>{iconLeft}</span>
        ) : null}

        {children}

        {iconRight && !isLoading && <span className={`ml-2`}>{iconRight}</span>}
      </button>
    );
  }
);

CustomButton.displayName = 'CustomButton';

export { CustomButton };
